import { useState } from "react";
import { FormControlLabel, Checkbox, Switch, Typography, Box, Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash, faSpinner, faChevronDown, faLanguage } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import "../translations.js";
import AddParameterModal from "./addParameterModal";
import ConfirmationModal from "./confirmationModal";
import InfoTooltip from "./InfoTooltip";
import { PRE_CHAT_LANGS, preChatTexts } from "../utils/preChatText";
import "./warmLeadConfig.css";
import "./notificationCenter.css";
// Shared section/card styles (.widget-sub-header, .control-group) live with the
// widget editor; import them so this section is styled regardless of order.
import "./widgetCustomizationTab.css";

// Enforce User Details — the widget's pre-chat gate.
//
// CONTROLLED component: the parent (WidgetCustomizationTab) owns the state so
// that toggling the gate or its required fields marks the Design tab dirty and
// only persists on the shared Save button — exactly like every other widget
// setting. Adding / deleting a field is a structural catalog op and is handled
// immediately by the parent's callbacks (it creates/removes a real parameter),
// which is why those have their own handlers rather than going through Save.
//
// Why this is a widget concern at all: the gate stores onto widget_config
// (require_pre_chat / pre_chat_required_fields) and is rendered by the widget
// itself (ultimo-bots-widget .saicf-pre-chat-container) before the chat opens.

// Mirror widgetCustomizationTab's ACCORDION_* exactly so this section blends
// into the widget editor's accordion list (16px divider between sections).
const ACCORDION_SX = {
  background: "transparent",
  boxShadow: "none",
  border: "none",
  borderRadius: 0,
  "&:before": { display: "none" },
  marginTop: 0,
  marginBottom: "16px",
  paddingBottom: "16px",
  borderBottom: "1px solid #e0e0e0",
  "&.Mui-expanded": {
    marginTop: 0,
    marginBottom: "16px",
  },
};

const ACCORDION_SUMMARY_SX = {
  padding: 0,
  minHeight: "unset",
  transition: "margin-bottom 0.3s ease",
  "& .MuiAccordionSummary-content": {
    margin: 0,
    "&.Mui-expanded": { margin: 0 },
  },
  "&.Mui-expanded": { minHeight: "unset", marginBottom: "10px" },
  "& .MuiAccordionSummary-expandIconWrapper": {
    color: "#5616ea",
    fontSize: "18px",
  },
};

const ACCORDION_DETAILS_SX = {
  padding: 0,
  paddingTop: "8px",
};

const SWITCH_SX = {
  "& .MuiSwitch-switchBase.Mui-checked": { color: "#5616ea" },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#5616ea" },
  "& .MuiSwitch-track": { backgroundColor: "#ccc" },
};

const EnforceUserDetails = ({
  loading = false,
  requirePreChat = false,
  requiredFields = [],
  parameters = [],
  anyLanguageNote = false,
  fieldLabels = {},
  onToggleAnyLanguage,
  onEditFieldLabel,
  onToggleRequire,
  onToggleField,
  onAddParameter,
  onDeleteParameter,
}) => {
  const { t, i18n } = useTranslation();
  // the field whose translations are open (one at a time)
  const [translating, setTranslating] = useState(null);
  const [expanded, setExpanded] = useState(true);
  const [isAddParameterModalOpen, setIsAddParameterModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [parameterToDelete, setParameterToDelete] = useState(null);

  const openDeleteModal = (paramId) => {
    setParameterToDelete(paramId);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (parameterToDelete != null) {
      await onDeleteParameter?.(parameterToDelete);
      setParameterToDelete(null);
    }
  };

  return (
    <>
      <Accordion
        expanded={expanded}
        onChange={(_e, isExpanded) => setExpanded(isExpanded)}
        disableGutters
        elevation={0}
        sx={ACCORDION_SX}
      >
        <AccordionSummary expandIcon={<FontAwesomeIcon icon={faChevronDown} />} sx={ACCORDION_SUMMARY_SX}>
          <h4 className="widget-sub-header" style={{ margin: 0, borderBottom: "none", paddingBottom: 0 }}>
            {t("enforce-contact-details-header")}
            <InfoTooltip text={t("enforce-contact-details-tooltip")} />
          </h4>
        </AccordionSummary>
        <AccordionDetails sx={ACCORDION_DETAILS_SX}>
          {loading ? (
            <Box display="flex" justifyContent="center" py={2}>
              <FontAwesomeIcon icon={faSpinner} spin style={{ color: "#5616ea", fontSize: 20 }} />
            </Box>
          ) : (
            <Box className="control-group">
              <Box display="flex" alignItems="center" gap={1.5}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={requirePreChat}
                      onChange={(e) => onToggleRequire?.(e.target.checked)}
                      sx={SWITCH_SX}
                    />
                  }
                  label={t("require-pre-chat")}
                />
              </Box>

              {requirePreChat && (
                <Box className="required-fields-section">
                  <Typography variant="body2" fontWeight={600} color="textPrimary" sx={{ marginBottom: "12px" }}>
                    {t("select-required-fields")}
                  </Typography>
                  {parameters.length === 0 ? (
                    <Typography variant="body2" color="textSecondary" fontStyle="italic">
                      {t("no-active-parameters")}
                    </Typography>
                  ) : (
                    <Box className="required-fields-list">
                      {parameters.map((param) => {
                        const open = translating === param.id;
                        const label = param.user_display || param.name;
                        return (
                          // one card per field; its translations fold open inside it (Robert, 18.10.2026)
                          <div key={param.id} className={`prechat-field ${open ? "is-open" : ""} ${param.pending ? "is-pending" : ""}`}>
                            <div className="prechat-field-row">
                              {param.pending ? (
                                // just added: the backend saves it and translates its label – then it is selected
                                <span className="prechat-field-pending" role="status" aria-label={t("prechat_field_adding")}>
                                  <FontAwesomeIcon icon={faSpinner} spin className="prechat-field-spinner" />
                                  <span>{label}</span>
                                </span>
                              ) : (
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={requiredFields.includes(param.id)}
                                      onChange={() => onToggleField?.(param.id)}
                                      sx={{
                                        "&.Mui-checked": { color: "#5616ea" },
                                        "&:hover": { backgroundColor: "#5616ea15" },
                                      }}
                                    />
                                  }
                                  label={label}
                                  sx={{ margin: 0, flex: 1 }}
                                />
                              )}
                              {!param.pending && (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => setTranslating(open ? null : param.id)}
                                    className="prechat-translate-button"
                                    title={t("prechat_translations")}
                                    aria-expanded={open}
                                    aria-controls={`prechat-fold-${param.id}`}
                                  >
                                    <FontAwesomeIcon icon={faLanguage} />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => openDeleteModal(param.id)}
                                    className="delete-parameter-button"
                                    title={t("delete")}
                                  >
                                    <FontAwesomeIcon icon={faTrash} />
                                  </button>
                                </>
                              )}
                            </div>
                            {!param.pending && (
                              <div id={`prechat-fold-${param.id}`} className="prechat-field-fold" aria-hidden={!open}
                                {...(open ? {} : { inert: "" })}>
                                <div className="prechat-field-fold-inner">
                                  <div className="prechat-translations">
                                    <p className="prechat-translations-hint">
                                      {Object.keys(fieldLabels[param.id] || {}).length
                                        ? t("prechat_translations_hint")
                                        : t("prechat_translations_pending")}
                                    </p>
                                    <div className="prechat-translations-grid">
                                      {PRE_CHAT_LANGS.map(([lang, name]) => (
                                        <label key={lang} className="prechat-translation">
                                          <span>{name}</span>
                                          <input
                                            type="text"
                                            maxLength={120}
                                            value={(fieldLabels[param.id] || {})[lang] || ""}
                                            placeholder={label}
                                            onChange={(e) => onEditFieldLabel?.(param.id, lang, e.target.value)}
                                          />
                                        </label>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </Box>
                  )}

                  <button
                    type="button"
                    onClick={() => setIsAddParameterModalOpen(true)}
                    className="add-parameter-button-icon"
                    title={t("add_parameter")}
                    style={{ marginTop: "12px" }}
                  >
                    <FontAwesomeIcon icon={faPlus} />
                  </button>

                  {/* the form appears in the visitor's language; this note tells them they can write in any */}
                  <Box sx={{ marginTop: "16px" }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={anyLanguageNote}
                          onChange={(e) => onToggleAnyLanguage?.(e.target.checked)}
                          sx={SWITCH_SX}
                        />
                      }
                      label={t("prechat_any_language_toggle", { note: preChatTexts(i18n.language).anyLanguage })}
                    />
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      <AddParameterModal
        open={isAddParameterModalOpen}
        onClose={() => setIsAddParameterModalOpen(false)}
        onSubmit={(formData) => onAddParameter?.(formData)}
        existingNames={parameters.map((p) => p.name)}
      />

      <ConfirmationModal
        open={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setParameterToDelete(null);
        }}
        onConfirm={confirmDelete}
        itemName={parameters.find((p) => p.id === parameterToDelete)?.name || "this field"}
        requireTextConfirmation={false}
      />
    </>
  );
};

export default EnforceUserDetails;
